import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { getPushStatus, enablePush, disablePush, testPush } from "../lib/push";
import { toast } from "sonner";

export default function PushToggle() {
  const [status, setStatus] = useState({ supported: true, enabled: false });
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    getPushStatus()
      .then(setStatus)
      .catch(() => setStatus({ supported: false, enabled: false }));

  useEffect(() => {
    refresh();
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (status.enabled) {
        await disablePush();
        toast.success("Notifiche push disattivate su questo dispositivo");
      } else {
        await enablePush();
        toast.success("Notifiche push attivate su questo dispositivo");
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || "Operazione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    setBusy(true);
    try {
      await testPush();
      toast.success("Notifica di prova inviata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  if (!status.supported) {
    return (
      <p className="text-sm text-slate-500" data-testid="push-unsupported">
        Questo dispositivo/browser non supporta le notifiche push. Su iPhone installa prima l'app nella schermata Home (Aggiungi a Home), poi riapri questa pagina.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {status.enabled ? <Bell className="h-5 w-5 text-gold" /> : <BellOff className="h-5 w-5 text-slate-400" />}
          <span className="text-sm text-navy" data-testid="push-status-label">
            {status.enabled ? "Attive su questo dispositivo" : "Disattivate su questo dispositivo"}
          </span>
        </div>
        <button
          data-testid="push-toggle-btn"
          onClick={toggle}
          disabled={busy}
          className={`flex h-11 items-center gap-2 rounded-lg px-4 text-sm font-semibold disabled:opacity-60 ${
            status.enabled ? "bg-slate-100 text-navy hover:bg-slate-200" : "bg-navy text-white hover:bg-navy-hover"
          }`}
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {status.enabled ? "Disattiva" : "Attiva notifiche"}
        </button>
      </div>
      {status.enabled && (
        <button
          data-testid="push-test-btn"
          onClick={doTest}
          disabled={busy}
          className="flex h-10 items-center gap-2 rounded-lg bg-slate-100 px-4 text-sm font-medium text-navy hover:bg-slate-200 disabled:opacity-60"
        >
          <Send className="h-4 w-4" /> Invia notifica di prova
        </button>
      )}
      {status.permission === "denied" && (
        <p className="text-sm text-amber-600" data-testid="push-denied-hint">
          Hai bloccato le notifiche per questo sito. Riattivale dalle impostazioni del telefono/browser (Notifiche → consenti), poi torna qui e premi "Attiva notifiche".
        </p>
      )}
    </div>
  );
}
